import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import Icon from '../icon/icon.component';
import { identity, getScreenSize } from '../utils/sizes';
import {
  defaultContext,
  getInitialStyle,
  getFocusedStyle,
  getUnFocusedStyle,
  ICON_WIDTH,
  ICON_HEIGHT,
  PADDING,
  GAP,
} from './bigButton.style';
import { ButtonBackground } from '../callToAction/background/buttonBackground.component';

/**
 * Component providing a possibility for a user to interact with the app. Could contain icon and/or text label.
 */
export class BigButton extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Background: {
          type: ButtonBackground,
          w: identity,
        },
        Fill: {},
        IconLeft: {
          type: Icon,
        },
        IconRight: {
          type: Icon,
        },
        Label: {},
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._style = defaultContext;
    this._isLeftIconTxLoading = false;
    this._isRightIconTxLoading = false;
    this._isLabelTxLoading = false;
    this._minWidth = 0;
    this._isFocused = false;
    this._isSelected = false;
  }

  _init() {
    const label = this.tag('Label');
    const leftIcon = this.tag('IconLeft');
    const rightIcon = this.tag('IconRight');

    const screenSize = getScreenSize();
    const iconWidth = ICON_WIDTH[screenSize];
    const gap = GAP[screenSize];
    const padding = PADDING[screenSize];

    const handleUpdate = () => {
      if (this._isLeftIconTxLoading || this._isLabelTxLoading || this._isRightIconTxLoading) {
        return;
      }

      const leftIconWidth = leftIcon.finalW ? gap + iconWidth : 0;
      const rightIconWidth = rightIcon.finalW ? gap + iconWidth : 0;
      const textWidth = this.finalW - (padding * 2 + leftIconWidth + rightIconWidth);

      this.patch({
        IconLeft: {
          x: padding,
        },
        IconRight: {
          x: this.finalW - padding,
        },
        Label: {
          text: {
            wordWrapWidth: textWidth,
          },
        },
      });
    };

    leftIcon.on('txLoaded', () => {
      this._isLeftIconTxLoading = false;
      handleUpdate();
    });

    rightIcon.on('txLoaded', () => {
      this._isRightIconTxLoading = false;
      handleUpdate();
    });

    label.on('txLoaded', () => {
      this._isLabelTxLoading = false;
      handleUpdate();
    });
  }

  _detach() {
    this.tag('Label').removeAllListeners('txLoaded');
    this.tag('IconLeft').removeAllListeners('txLoaded');
    this.tag('IconRight').removeAllListeners('txLoaded');
  }

  set label(value) {
    this.tag('Label').text.text = value;
    this._isLabelTxLoading = true;
  }

  set pictoLeft(value) {
    this._isLeftIconTxLoading = true;
    const screenSize = getScreenSize();

    this.tag('IconLeft').iconData = {
      picto: value,
      viewBox: {
        w: ICON_WIDTH[screenSize],
        h: ICON_HEIGHT[screenSize],
      },
    };
  }

  set pictoRight(value) {
    this._isRightIconTxLoading = true;
    const screenSize = getScreenSize();

    this.tag('IconRight').iconData = {
      picto: value,
      viewBox: {
        w: ICON_WIDTH[screenSize],
        h: ICON_HEIGHT[screenSize],
      },
    };
  }

  get colorMode() {
    return this._style.colorMode;
  }

  set colorMode(value) {
    this._style = { ...this._style, colorMode: value };
    this.patch(getInitialStyle(this._style));
  }

  get isSelected() {
    return this._isSelected;
  }

  set isSelected(value) {
    this._isSelected = value;
    this._updateStyle();
  }

  _updateStyle() {
    this.patch(
      this._isFocused
        ? getFocusedStyle({ ...this._style, selected: this._isSelected })
        : getUnFocusedStyle({ ...this._style, selected: this._isSelected }),
    );
  }

  _focus() {
    this._isFocused = true;
    this._updateStyle();
  }

  _unfocus() {
    this._isFocused = false;
    this._updateStyle();
  }
}
